import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { GroupedRow } from '../../screens/AnalyticsHub/constants/onePagerConstants';
import type { OnePagerResponseRowDTO } from '../../screens/AnalyticsHub/types/onePagerTypes';

export type MobileViewBy = 'region' | 'market' | 'location';

export interface OnePagerMobileState {
	selectedViewBy: MobileViewBy;
	content: OnePagerResponseRowDTO[];
	rows: GroupedRow[];
	grandTotalRow: GroupedRow | null;
	totalRows: number;
	page: number;
	size: number;
	loading: boolean;
	error: string | null;
}

const initialState: OnePagerMobileState = {
	selectedViewBy: 'region',
	content: [],
	rows: [],
	grandTotalRow: null,
	totalRows: 0,
	page: 1,
	size: 25,
	loading: false,
	error: null,
};

const onePagerMobileSlice = createSlice({
	name: 'onePagerMobile',
	initialState,
	reducers: {
		setMobileViewBy(state, action: PayloadAction<MobileViewBy>) {
			state.selectedViewBy = action.payload;
			state.page = 1;
		},
		setMobilePage(state, action: PayloadAction<number>) {
			state.page = Math.max(1, action.payload);
		},
		setMobileSize(state, action: PayloadAction<number>) {
			state.size = action.payload;
			state.page = 1;
		},
		fetchMobileStart(state) {
			state.loading = true;
			state.error = null;
		},
		fetchMobileSuccess(
			state,
			action: PayloadAction<{
				content: OnePagerResponseRowDTO[];
				rows: GroupedRow[];
				grandTotalRow: GroupedRow | null;
				totalRows: number;
				page: number;
				size: number;
			}>
		) {
			state.loading = false;
			state.error = null;
			state.content = action.payload.content;
			state.rows = action.payload.rows;
			state.grandTotalRow = action.payload.grandTotalRow;
			state.totalRows = action.payload.totalRows;
			state.page = action.payload.page;
			state.size = action.payload.size;
		},
		fetchMobileError(state, action: PayloadAction<string>) {
			state.loading = false;
			state.error = action.payload;
			state.content = [];
			state.rows = [];
			state.grandTotalRow = null;
			state.totalRows = 0;
		},
		resetOnePagerMobileState() {
			return { ...initialState };
		},
	},
});

export const {
	setMobileViewBy,
	setMobilePage,
	setMobileSize,
	fetchMobileStart,
	fetchMobileSuccess,
	fetchMobileError,
	resetOnePagerMobileState,
} = onePagerMobileSlice.actions;

export default onePagerMobileSlice.reducer;

type RootStateWithOnePagerMobile = { onePagerMobile: OnePagerMobileState };

export const selectMobileViewBy = (state: RootStateWithOnePagerMobile) => state.onePagerMobile.selectedViewBy;
export const selectMobileContent = (state: RootStateWithOnePagerMobile) => state.onePagerMobile.content;
export const selectMobileRows = (state: RootStateWithOnePagerMobile) => state.onePagerMobile.rows;
export const selectMobileGrandTotalRow = (state: RootStateWithOnePagerMobile) => state.onePagerMobile.grandTotalRow;
export const selectMobileTotalRows = (state: RootStateWithOnePagerMobile) => state.onePagerMobile.totalRows;
export const selectMobilePage = (state: RootStateWithOnePagerMobile) => state.onePagerMobile.page;
export const selectMobileSize = (state: RootStateWithOnePagerMobile) => state.onePagerMobile.size;
export const selectMobileLoading = (state: RootStateWithOnePagerMobile) => state.onePagerMobile.loading;
export const selectMobileError = (state: RootStateWithOnePagerMobile) => state.onePagerMobile.error;
